import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RECORD_FIELDS } from '../recordsApi';

const ID_FIELD = { key: 'DaftId__c', label: 'Daft ID', type: 'text', placeholder: 'Leave blank to generate one' };

const toInputValue = (value) => (value == null ? '' : String(value));

// Builds the payload of fields that differ from `record` (all non-blank fields when record is null).
// Throws an Error with a user-facing message if a numeric field is not a number.
function buildPayload(fields, values, record) {
  const payload = {};
  for (const f of fields) {
    if (values[f.key] === toInputValue(record?.[f.key])) continue;
    const raw = values[f.key];
    if (f.type !== 'number') {
      payload[f.key] = raw;
      continue;
    }
    // Pasted numbers may carry spaces or a decimal comma (53,3224)
    const cleaned = raw.trim().replace(/\s+/g, '').replace(',', '.');
    if (cleaned === '') {
      payload[f.key] = null;
    } else if (Number.isNaN(Number(cleaned))) {
      throw new Error(`${f.label} must be a number`);
    } else {
      payload[f.key] = Number(cleaned);
    }
  }
  return payload;
}

/**
 * Shared form for editing an existing record and creating a new one.
 *  - record: existing record (edit) or null (new)
 *  - includeId: show an editable Daft ID field (new only)
 *  - media: block rendered between the header and the fields (image/map or drop zone)
 *  - onSave(payload): async; throw an Error to show its message above the form
 */
export default function RecordForm({ title, subtitle, record = null, includeId = false, media, cancelTo, saveLabel = 'Save changes', onSave }) {
  const fields = includeId ? [ID_FIELD, ...RECORD_FIELDS] : RECORD_FIELDS;
  const [values, setValues] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, toInputValue(record?.[f.key])]))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    let payload;
    try {
      payload = buildPayload(fields, values, record);
    } catch (err) {
      setError(err.message);
      return;
    }
    setSaving(true);
    try {
      await onSave(payload);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <>
      <div className="rec-header">
        <div>
          <h1 className="rec-edit-title">{title}</h1>
          {subtitle && <p className="rec-muted rec-sm rec-edit-subtitle">{subtitle}</p>}
        </div>
        <div className="rec-form-actions rec-form-actions-top">
          <button type="submit" form="record-form" disabled={saving} className="rec-btn-primary">
            {saving ? 'Saving…' : saveLabel}
          </button>
          <Link to={cancelTo} className="rec-btn">Cancel</Link>
        </div>
      </div>

      {error && <p className="rec-error rec-sm rec-banner">{error}</p>}

      {media}

      <form id="record-form" onSubmit={handleSubmit} className="rec-form">
        {fields.map((f) => (
          <div key={f.key}>
            <label htmlFor={f.key}>{f.label}</label>
            <input
              id={f.key}
              type="text"
              inputMode={f.type === 'number' ? 'decimal' : undefined}
              placeholder={f.placeholder}
              value={values[f.key]}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            />
          </div>
        ))}
      </form>
    </>
  );
}
